import React, {useEffect, useMemo, useState} from 'react';
import Modal from '../../../../../components-new/ui/Modal';
import Translate, {t} from '../../../../../components/Translate';
import config from "../../../../../config";
import {generalModalStyles} from "../../../../../utils/General";
import FileUpload from "../../../../../components-new/general/FileUpload";
import Input from "../../../../../components-new/form/Input";
import DropdownSelect from "../../../../../components-new/form/DropdownSelect";
import LoadingComponent from "../../../../../components-new/ui/LoadingComponent";
import colors from "../../../../../utils/Colors";
import ContactAvatar from "../../../../../components-new/general/ContactAvatar";
import SilverButton from "../../../../../components-new/buttons/SilverButton";
import BlueButton from "../../../../../components-new/buttons/BlueButton";
import {createDivision, updateDivision} from "../../../../../services/internalGroups/api";
import {GroupRole} from "../../../../../services/internalGroups/constants";
import Field from "../../../../../components-new/form/Field";

Modal.setAppElement('#root');

const DivisionModal = ({
                           company,
                           user,
                           users,
                           isOpen,
                           addNotification,
                           type, // create,update
                           closeModal,
                           isFetchingUsers,
                           onSuccess,
                           division,
                           departmentId
                       }) => {

    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState({
        name: "",
        description: "",
        headId: null,
        membersIds: []
    });

    useEffect(() => {
        if (type === 'update' && division) {
            const head = division.members ? division.members.find(m => m.groupRole === GroupRole.DIVISION_HEAD) : null;
            setForm({
                name: division.name || "",
                description: division.description || "",
                headId: head ? head.id : null,
                membersIds: division.members ? division.members.filter(m => m.groupRole !== GroupRole.DIVISION_HEAD).map(m => m.id) : []
            });
        } else {
            setForm({
                name: "",
                description: "",
                headId: null,
                membersIds: []
            });
        }
    }, [type, division, isOpen]);

    const handleCreate = () => {
        setLoading(true);
        const data = {
            ...form,
            membersIds: new Set(form.membersIds)
        };
        createDivision(departmentId, company.id, data).then(response => {
            if (response.success) {
                onSuccess(response.division, departmentId);
            }
        })
            .catch(e => {
                const response = e.response.data
                addNotification(t({text: 'error.' + response.message, user}), config.notificationLevels.error)
            })
            .finally(() => setLoading(false));
    }

    const handleUpdate = () => {
        setLoading(true);
        const data = {
            ...form,
            membersIds: new Set(form.membersIds)
        };
        updateDivision(division.id, company.id, data).then(response => {
            if (response.success) {
                onSuccess(response.division, departmentId);
            }
        })
            .catch(e => {
                const response = e.response.data
                addNotification(t({text: 'error.' + response.message, user}), config.notificationLevels.error)
            })
            .finally(() => setLoading(false));
    }

    const computedOptionsForHead = useMemo(() => {
        if (!users) return []
        if (type === 'create') {
            return users.filter(u => (!u.memberships || u.memberships.length === 0) && !form.membersIds.some(id => id === u.id))
        }
        if (!division) return []
        return users.filter(u => (!u.memberships || u.memberships.length === 0 || u.memberships.filter(m => m.groupId !== division.id).length === 0) && !form.membersIds.some(id => id === u.id))
    }, [users, type, form, division])

    const computedOptions = useMemo(() => {
        if (!users) return []
        if (type === 'create') {
            return users.filter(u => (!u.memberships || u.memberships.length === 0) && u.id !== form.headId)
        }
        if (!division) return []
        return users.filter(u => (!u.memberships || u.memberships.length === 0 || u.memberships.filter(m => m.groupId !== division.id).length === 0) && form.headId !== u.id)
    }, [users, type, form, division])

    return (
        <Modal closeTimeoutMS={config.closeTimeoutMS}
               isOpen={isOpen}
               style={{
                   ...generalModalStyles.standardModalStyles,
                   content: {
                       ...generalModalStyles.standardModalStyles.content,
                       maxWidth: '600px',
                       width: '90%',
                   }
               }}
               onRequestClose={closeModal}
               title={<Translate text={type === 'create' ? 'addDivision' : 'updateDivision'}/>}
               headerSeparator
               footerSeparator
               footer={<React.Fragment>
                   <SilverButton type="button" width="auto" height="40px" onClick={closeModal}>
                       <Translate text="cancel"/>
                   </SilverButton>
                   <BlueButton type="button" width="auto" height="40px"
                               onClick={type === 'create' ? handleCreate : handleUpdate}
                               disabled={!form.name}>
                       {loading ? <LoadingComponent size={24} stroke={'#fff'}/> : type === 'create' ?
                           <Translate text="create"/> : <Translate text="update"/>}
                   </BlueButton>
               </React.Fragment>}
        >
            <div className="internal-grouping__modal-form">
                    <Input
                        label={<Translate text="divisionName"/>}
                        required
                        value={form.name}
                        onChange={(e) => setForm({...form, name: e.target.value})}
                        placeholder={t({user, text: "divisionName"})}
                        maxLength={256}
                    />

                    <Input
                        label={<Translate text="description"/>}
                        value={form.description}
                        onChange={(e) => setForm({...form, description: e.target.value})}
                        isTextarea={true}
                        resize={"none"}
                        placeholder={t({user, text: "description"})}
                        style={{
                            height: '120px'
                        }}
                        maxLength={1024}
                    />

                    <Field label="selectDivisionHead">
                    <DropdownSelect
                        selectedValue={
                            users ? [users.find(u => u.id === form.headId)].filter(v => !!v).map(u => ({
                                    ...u,
                                    value: u.id
                                }))
                                :
                                null
                        }
                        onChange={(e) => {
                            setForm({...form, headId: form.headId === e.id ? null : e.id});
                        }}
                        options={computedOptionsForHead.map(u => ({...u, value: u.id}))}
                        searchBy={computedOptionsForHead.length > 5 ?
                            (option, search) => {
                                return !search || (option.firstName + ' ' + option.lastName).toLowerCase().includes(search.toLowerCase())
                            }
                            :
                            null
                        }
                        renderLabel={
                            u => (u && u.length > 0) ?
                                <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                                    <ContactAvatar
                                        contact={{
                                            firstName: u[0].firstName,
                                            lastName: u[0].lastName,
                                            thumbUrl: u[0].thumbUrl,
                                        }}
                                        variant="menu"
                                    />
                                    <span>{u[0].firstName + ' ' + u[0].lastName}</span>
                                </div>
                                :
                                isFetchingUsers ?
                                    <LoadingComponent/>
                                    :
                                    <span style={{color: colors.silverDark}}>
                                        <Translate text={'selectDivisionHead'}/>
                                    </span>
                        }
                        variant={'outline'}
                        styles={{
                            width: '100%',
                            height: '36px',
                        }}
                        optionsContainerStyles={{
                            maxHeight: '160px',
                        }}
                        direction={'top'}
                        closeOnOptionSelect={true}
                        classes="message-search-timeline-dropdow0n"
                    >
                        {option =>
                            <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                                <ContactAvatar
                                    contact={{
                                        firstName: option.firstName,
                                        lastName: option.lastName,
                                        thumbUrl: option.thumbUrl,
                                    }}
                                    variant="menu"
                                />
                                <span>{option.firstName + ' ' + option.lastName}</span>
                            </div>
                        }
                    </DropdownSelect>
                    </Field>

                    <Field label="selectMembers">
                    <DropdownSelect
                        selectedValue={
                            users ? users.filter(u => form.membersIds.includes(u.id)).map(u => ({
                                    ...u,
                                    value: u.id
                                }))
                                :
                                []
                        }
                        onChange={(e) => {
                            setForm(prev => {
                                const membersIds = prev.membersIds.includes(e.id)
                                    ? prev.membersIds.filter(id => id !== e.id)
                                    : [...prev.membersIds, e.id];
                                return {...prev, membersIds};
                            });
                        }}
                        options={computedOptions.map(u => ({...u, value: u.id}))}
                        searchBy={computedOptions.length > 5 ?
                            (option, search) => {
                                return !search || (option.firstName + ' ' + option.lastName).toLowerCase().includes(search.toLowerCase())
                            }
                            :
                            null
                        }
                        renderLabel={
                            selectedUsers => (selectedUsers && selectedUsers.length > 0) ?
                                <div style={{display: 'flex', gap: '4px', alignItems: 'center', overflow: 'hidden'}}>
                                    {selectedUsers.length} <Translate text="members"/>
                                </div>
                                :
                                isFetchingUsers ?
                                    <LoadingComponent/>
                                    :
                                    <span style={{color: colors.silverDark}}>
                                        <Translate text={'selectMembers'}/>
                                    </span>
                        }
                        variant={'outline'}
                        styles={{
                            width: '100%',
                            height: '36px',
                        }}
                        optionsContainerStyles={{
                            maxHeight: '200px',
                        }}
                        direction={'top'}
                        closeOnOptionSelect={false}
                        classes="message-search-timeline-dropdown"
                    >
                        {option =>
                            <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                                <ContactAvatar
                                    contact={{
                                        firstName: option.firstName,
                                        lastName: option.lastName,
                                        thumbUrl: option.thumbUrl,
                                    }}
                                    variant="menu"
                                />
                                <span>{option.firstName + ' ' + option.lastName}</span>
                            </div>
                        }
                    </DropdownSelect>
                    </Field>
            </div>
        </Modal>
    )
        ;
};

export default DivisionModal;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/internal-grouping/components/DivisionModal.jsx